import { useEffect, useRef } from 'react'

import { MarkerClusterer, type Renderer } from '@googlemaps/markerclusterer'
import { useGoogleMap } from '@react-google-maps/api'

import { DriverHomeLocationDto } from 'generated/graphql'

export interface DriverPopupData {
  fullName: string
  companyName: string
  telephoneNumber: string
  vehicleTypes: string[]
  statusLabel: string
  statusColor: string
}

interface DriverMarkerClusterProps {
  drivers: DriverHomeLocationDto[]
  visible: boolean
  color: string
  getDriverStatus: (lastActivityAt: string | null | undefined) => { label: string; color: string }
  onMarkerClick: (position: google.maps.LatLngLiteral, drivers: DriverPopupData[]) => void
}

const clusterDiameter = (count: number) => (count < 10 ? 36 : count < 50 ? 44 : 52)

const createClusterRenderer = (color: string): Renderer => ({
  render: ({ count, position }) => {
    const diameter = clusterDiameter(count)
    const svg = encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="${diameter}" height="${diameter}">` +
        `<circle cx="${diameter / 2}" cy="${diameter / 2}" r="${diameter / 2 - 2}" fill="${color}" fill-opacity="0.85" stroke="#ffffff" stroke-width="3" />` +
        `</svg>`,
    )

    return new google.maps.Marker({
      position,
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${svg}`,
        scaledSize: new google.maps.Size(diameter, diameter),
        anchor: new google.maps.Point(diameter / 2, diameter / 2),
      },
      label: { text: String(count), color: '#ffffff', fontSize: '13px', fontWeight: '700' },
      zIndex: 1000 + count,
    })
  },
})

const createDriverIcon = (color: string): google.maps.Symbol => ({
  path: google.maps.SymbolPath.CIRCLE,
  fillColor: color,
  fillOpacity: 0.9,
  strokeColor: '#fff',
  strokeWeight: 1.5,
  scale: 5,
})

const OVERLAP_OFFSET = 0.00008

const spreadOverlappingPositions = (drivers: DriverHomeLocationDto[]) => {
  const groups = new Map<string, DriverHomeLocationDto[]>()
  for (const driver of drivers) {
    const key = `${driver.lat.toFixed(5)},${driver.lng.toFixed(5)}`
    const group = groups.get(key)
    if (group) group.push(driver)
    else groups.set(key, [driver])
  }

  const positions = new Map<DriverHomeLocationDto, google.maps.LatLngLiteral>()
  for (const group of groups.values()) {
    group.forEach((driver, index) => {
      const angle = (2 * Math.PI * index) / group.length
      positions.set(driver, {
        lat: driver.lat + (group.length > 1 ? OVERLAP_OFFSET * Math.sin(angle) : 0),
        lng: driver.lng + (group.length > 1 ? OVERLAP_OFFSET * Math.cos(angle) : 0),
      })
    })
  }
  return positions
}

export const DriverMarkerCluster = ({
  drivers,
  visible,
  color,
  getDriverStatus,
  onMarkerClick,
}: DriverMarkerClusterProps) => {
  const map = useGoogleMap()
  const clustererRef = useRef<MarkerClusterer | null>(null)
  const driverDataRef = useRef(new Map<google.maps.Marker, DriverPopupData>())
  const callbacksRef = useRef({ getDriverStatus, onMarkerClick })
  callbacksRef.current = { getDriverStatus, onMarkerClick }

  useEffect(() => {
    if (!map) return

    const clusterer = new MarkerClusterer({
      map,
      renderer: createClusterRenderer(color),
      onClusterClick: (_event, cluster) => {
        const clusterDrivers = (cluster.markers ?? [])
          .map((marker) => driverDataRef.current.get(marker as google.maps.Marker))
          .filter((driver): driver is DriverPopupData => Boolean(driver))

        if (clusterDrivers.length > 0) {
          const position = cluster.position
          callbacksRef.current.onMarkerClick({ lat: position.lat(), lng: position.lng() }, clusterDrivers)
        }
      },
    })
    clustererRef.current = clusterer

    return () => {
      clusterer.clearMarkers()
      clustererRef.current = null
    }
  }, [map, color])

  useEffect(() => {
    const clusterer = clustererRef.current
    if (!clusterer) return

    clusterer.clearMarkers()
    driverDataRef.current = new Map()

    if (!visible || drivers.length === 0) return

    const icon = createDriverIcon(color)
    const positions = spreadOverlappingPositions(drivers)

    const markers = drivers.map((driver) => {
      const position = positions.get(driver) ?? { lat: driver.lat, lng: driver.lng }
      const marker = new google.maps.Marker({ position, icon })
      const status = callbacksRef.current.getDriverStatus(driver.lastActivityAt as string | null | undefined)

      const data: DriverPopupData = {
        fullName: driver.fullName,
        companyName: driver.companyName,
        telephoneNumber: driver.telephoneNumber,
        vehicleTypes: driver.vehicleTypes,
        statusLabel: status.label,
        statusColor: status.color,
      }
      driverDataRef.current.set(marker, data)
      marker.addListener('click', () => callbacksRef.current.onMarkerClick(position, [data]))

      return marker
    })

    clusterer.addMarkers(markers)
  }, [drivers, visible, color])

  return null
}
